import {uuidv4} from '../utils';

export type ModalCallback<TResult> = (result: TResult) => void;
export type ModalStackInfo<TProps, TResult> = {
  modalId: string;
  callback: ModalCallback<TResult>;
  Component: React.FC<TProps>;
  props: TProps;
};

const _modalStack: ModalStackInfo<any, any>[] = [];
const _listeners: {
  [key: string]: (modals: ModalStackInfo<any, any>[]) => void;
} = {};

/**
 * Adds a notification listener for changes in the modal stack
 * @param callback Callback when modal list has changed
 * @returns Identifier of subscription @see removeModalListener
 */
export const addModalListener = (
  callback: (modals: ModalStackInfo<any, any>[]) => void,
): string => {
  const listenerId = uuidv4();
  _listeners[listenerId] = callback;
  return listenerId;
};

/**
 * Removes a modal listener
 * @param listenerId
 */
export const removeModalListener = (listenerId: string) => {
  delete _listeners[listenerId];
};

/**
 * Pushes a new modal information object on the modal stack
 * @param callback Callback to the modal when modal is done
 * @param Component Component to render modal
 * @param props Props for the component
 */
export const pushModal = <TProps, TResult>(
  callback: ModalCallback<TResult>,
  Component: React.FC<TProps>,
  props: TProps,
) => {
  const modal = {
    modalId: uuidv4(),
    callback,
    Component,
    props: {...props, callback},
  };
  _modalStack.push(modal);
  notifyListeners();
};

/**
 * Pops the top-most modal from the modal stack
 */
export const popModal = () => {
  _modalStack.pop();
  notifyListeners();
};

const notifyListeners = () => {
  Object.keys(_listeners).map(key => _listeners[key](_modalStack));
};
